import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useFormik } from 'formik';
import styled from 'styled-components';
import * as Yup from 'yup';
import { AuthCodeInput, AuthContainer, AuthContent, AuthContentInner, AuthHeader, AuthMarketing, Button, ButtonRetry, LayoutSplit, Link } from 'components';
import { appConfig } from 'config/app';
import { lineHeight, theme, toRem } from 'design';
import { routeConstants } from 'routes';
import Form from 'screens/Error/Form';
import * as accountRecoveryActions from 'store/AccountRecovery/actions';
import * as ActionTypes from 'store/AccountRecovery/types';
import { selectAPIRequestErrors, selectIsLoadingAPIRequest } from 'store/ApiClient';
import { IAccountRecoveryState, IStore } from 'store/types';

const StyledLink = styled(Link)`
  font-weight: ${theme.font.weight.semiBold};
  font-size: ${toRem(theme.font.size.md)};
  line-height: ${lineHeight(theme.font.size.md)};
`;

const StyledButton = styled(Button)`
  margin-top: ${toRem(theme.space['2xl'])};
`;

const StyledButtonRetry = styled(ButtonRetry)`
  margin-top: ${toRem(theme.space.sm)};
`;

interface ISignUpEmailVerificationProps {
  accountRecovery: IAccountRecoveryState;
  onBack: () => void;
}

const AccountRecoveryEmailVerification: React.FC<ISignUpEmailVerificationProps> = ({ accountRecovery, onBack }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const isLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, ActionTypes.AccountRecovery.EMAIL_VERIFY_REQUEST));
  const apiErrorsVerify = useSelector((state: IStore) => selectAPIRequestErrors(state, ActionTypes.AccountRecovery.EMAIL_VERIFY_REQUEST));
  const resendLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, ActionTypes.AccountRecovery.RESEND_EMAIL_VERIFICATION_CODE_REQUEST));

  const validationSchema = Yup.object().shape({
    code: Yup.string().required('A 6 digit code must be entered').min(6, 'A 6 digit code must be entered').max(6, 'A 6 digit code must be entered'),
  });

  const { handleSubmit, dirty, isValid, setFieldValue } = useFormik({
    initialValues: {
      code: '',
    },
    onSubmit: (values: { code: string }) => {
      onSubmit(values.code);
    },
    validateOnChange: true,
    validationSchema,
  });

  const onSubmit = (verificationCode: string) => {
    const payload = {
      verifyCode: verificationCode,
    };

    dispatch(accountRecoveryActions.verifyEmailRequest(accountRecovery.id, payload, onSuccess));
  };

  const resendEmail = () => dispatch(accountRecoveryActions.resendEmailVerificationCodeRequest(accountRecovery.id));

  const onSuccess = () => {
    history.push(routeConstants.public.accountRecovery.addMobileIdentity);
  };

  return (
    <LayoutSplit
      left={
        <AuthContainer>
          <AuthContent className="email-verify" data-cy="account-recovery-email-verify-form">
            <AuthContentInner>
              <AuthHeader data-cy="confirm-identity-header" title="Confirm Your Email Address" subtitle="Enter the code sent to your email" onBack={onBack} value={accountRecovery.identity.value} />
              <Form onSubmit={handleSubmit}>
                <AuthCodeInput errorMessages={apiErrorsVerify?.errorMessages} onChange={(val) => setFieldValue('code', val)} />
                <StyledButton data-cy="submit-button" type="submit" label="Continue" disabled={isLoading || !(isValid && dirty)} isProcessing={isLoading} />
              </Form>
              <StyledButtonRetry dataCy={resendLoading ? 'resend-verification-code-loading' : 'resend-verification-code'} label="Resend verification code" onClick={resendEmail} isLoading={resendLoading} />
              <StyledLink as="a" target="_blank" rel="noopener noreferrer" href={appConfig.LINKS.SUPPORT}>
                Need some help?
              </StyledLink>
            </AuthContentInner>
          </AuthContent>
        </AuthContainer>
      }
      right={<AuthMarketing />}
    />
  );
};

export default AccountRecoveryEmailVerification;
